/**
 * Utility functions for generating audio waveforms from song data
 */

/**
 * Analyzes audio data and generates waveform data points
 * @param {string} filePath - Path to the audio file
 * @param {number} numPoints - Number of data points to generate
 * @returns {Promise<number[]>} Array of normalized amplitude values (0-100)
 */
export const generateWaveformData = async (filePath, numPoints = 48) => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Get the audio file
    const response = await fetch(filePath);
    const arrayBuffer = await response.arrayBuffer();
    
    // Decode the audio data
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // Get the raw audio data
    const rawData = audioBuffer.getChannelData(0); // Use first channel
    const blockSize = Math.floor(rawData.length / numPoints);
    const waveformData = [];

    // Process the audio data in blocks
    for (let i = 0; i < numPoints; i++) {
      const startIndex = i * blockSize;
      const endIndex = Math.min((i + 1) * blockSize, rawData.length);
      
      // Find the peak amplitude in this block
      let maxAmplitude = 0;
      for (let j = startIndex; j < endIndex; j++) {
        const amplitude = Math.abs(rawData[j]);
        if (amplitude > maxAmplitude) {
          maxAmplitude = amplitude;
        }
      }
      
      // Normalize to 0-100 range and add to result
      waveformData.push(Math.round(maxAmplitude * 100));
    }

    // Normalize the entire waveform to ensure good visualization
    const maxValue = Math.max(...waveformData);
    const normalizedData = waveformData.map(value => {
      // Scale to 20-90 range for better visualization
      return Math.round(20 + (value / maxValue) * 70);
    });

    // Smooth out the data
    const smoothedData = smoothWaveform(normalizedData);
    
    // Close the audio context
    await audioContext.close();
    
    return smoothedData;
  } catch (error) {
    console.error('Error generating waveform:', error);
    throw error;
  }
};

/**
 * Applies smoothing to the waveform data to prevent jarring transitions
 * @param {number[]} data - The waveform data to smooth
 * @returns {number[]} Smoothed waveform data
 */
const smoothWaveform = (data) => {
  const smoothed = [...data];
  
  // Apply moving average smoothing
  for (let i = 1; i < data.length - 1; i++) {
    smoothed[i] = Math.round(
      (data[i - 1] + data[i] * 2 + data[i + 1]) / 4
    );
  }
  
  return smoothed;
}; 